import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getAppliedPracticeDetail, type AppliedPracticeDetail, type AppliedDifficulty } from '../services/conceptPlus1Service';
import AppliedPracticeHistoryCard from '../components/AppliedPracticeHistoryCard';
import { ArrowLeft } from 'lucide-react';

export default function AppliedPracticeHistoryDetail() {
  const { session, isLoading } = useAuth();
  const { conceptId, difficulty } = useParams<{ conceptId: string; difficulty: string }>();
  const [detail, setDetail] = useState<AppliedPracticeDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session || !conceptId || !difficulty) return;
    getAppliedPracticeDetail(session.student_id, parseInt(conceptId, 10), difficulty as AppliedDifficulty).then((data) => {
      setDetail(data);
      setLoading(false);
    });
  }, [session?.student_id, conceptId, difficulty]);

  if (isLoading || loading) {
    return (
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-coral-warm mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Loading practice set…</p>
      </main>
    );
  }

  if (!detail || !detail.success) {
    return (
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-8 text-center">
        <p className="text-gray-600 mb-4">Couldn't find that practice set.</p>
        <Link to="/practice-history" className="text-coral-warm font-medium hover:underline">
          Back to Practice History
        </Link>
      </main>
    );
  }

  return (
    <main className="max-w-2xl mx-auto w-full px-4 sm:px-6 pt-24 pb-8">
      <Link to="/practice-history" className="inline-flex items-center gap-1.5 text-sm text-coral-warm hover:text-orange-600 font-medium mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back to Practice History
      </Link>

      <h1 className="text-xl font-bold text-ocean-deep mb-1">{detail.concept_name}</h1>
      <p className="text-sm text-gray-500 mb-6">{detail.difficulty} · {detail.questions.length} questions</p>

      <div className="space-y-3">
        {detail.questions.map((q) => (
          <AppliedPracticeHistoryCard key={q.question_index} q={q} index={q.question_index} />
        ))}
      </div>
    </main>
  );
}
